export const WORKSPACE_DOCUMENT_VERSION = 1;
export const SAVED_DOCUMENT_VERSION = 1;
export const PRESET_INTERCHANGE_FORMAT = "fishystuff-user-presets.v1";
export const LEGACY_STORAGE_KEY = "fishystuff.user-presets.v1";
export const HISTORY_ACTION_LIMIT = 100;

const TOKEN_PATTERN = /^[a-z0-9]+(?:[._-][a-z0-9]+)*$/;
const UTC_RFC3339_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,9}))?Z$/;
const SAVED_PRESET_EDGE_WHITESPACE_PATTERN = /^(?:[\u0009-\u000D\u0020\u0085\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000\uFEFF])|(?:[\u0009-\u000D\u0020\u0085\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000\uFEFF])$/u;
const RUNTIME_FIXED_PRESET_DESCRIPTOR_FIELDS = Object.freeze(["id", "name"]);

export function isRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

export function exactFields(value, fields) {
  if (!isRecord(value)) {
    return false;
  }
  const actual = Object.keys(value).sort();
  const expected = fields.slice().sort();
  return actual.length === expected.length
    && actual.every((field, index) => field === expected[index]);
}

export function cloneValue(value) {
  if (value === undefined) {
    return undefined;
  }
  if (typeof structuredClone === "function") {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value));
}

export function deepFreeze(value, seen = new WeakSet()) {
  if (!value || typeof value !== "object" || seen.has(value)) {
    return value;
  }
  seen.add(value);
  for (const child of Object.values(value)) {
    deepFreeze(child, seen);
  }
  return Object.freeze(value);
}

export function frozenClone(value) {
  return deepFreeze(cloneValue(value));
}

export function boundedText(value, label, { max = 256, allowEmpty = false } = {}) {
  if (typeof value !== "string") {
    throw new TypeError(`${label} must be text.`);
  }
  const text = value.trim();
  if ((!allowEmpty && !text) || text.length > max) {
    throw new TypeError(`${label} must be ${allowEmpty ? "a" : "non-empty"} bounded string.`);
  }
  return text;
}

function hasWellFormedUnicode(value) {
  if (typeof value.isWellFormed === "function") return value.isWellFormed();
  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index);
    if (codeUnit >= 0xD800 && codeUnit <= 0xDBFF) {
      const next = value.charCodeAt(index + 1);
      if (next < 0xDC00 || next > 0xDFFF) return false;
      index += 1;
    } else if (codeUnit >= 0xDC00 && codeUnit <= 0xDFFF) {
      return false;
    }
  }
  return true;
}

export function savedPresetName(value, label = "Saved preset name") {
  if (
    typeof value !== "string"
    || value.length === 0
    || value.length > 120
    || value.includes("\u0000")
    || SAVED_PRESET_EDGE_WHITESPACE_PATTERN.test(value)
    || !hasWellFormedUnicode(value)
  ) {
    throw new TypeError(`${label} must be canonical text within 120 UTF-16 code units.`);
  }
  return value;
}

export function collectionKey(value) {
  const key = boundedText(value, "Preset collection key", { max: 96 });
  if (key !== value || !TOKEN_PATTERN.test(key)) {
    throw new TypeError("Preset collection key must be a canonical lowercase token.");
  }
  return key;
}

export function opaqueId(value, label = "ID") {
  const id = boundedText(value, label, { max: 160 });
  if (id !== value) {
    throw new TypeError(`${label} must already be canonical.`);
  }
  return id;
}

export function isoTimestamp(value, label = "Timestamp") {
  const timestamp = boundedText(value, label, { max: 64 });
  const match = UTC_RFC3339_PATTERN.exec(timestamp);
  if (!match) {
    throw new TypeError(`${label} must be an ISO timestamp.`);
  }
  const [, year, month, day, hour, minute, second, fraction = ""] = match;
  const milliseconds = fraction.padEnd(3, "0").slice(0, 3);
  const millisecondTimestamp = `${year}-${month}-${day}T${hour}:${minute}:${second}.${milliseconds}Z`;
  const parsed = new Date(millisecondTimestamp);
  if (
    !Number.isFinite(parsed.getTime())
    || parsed.toISOString() !== millisecondTimestamp
  ) {
    throw new TypeError(`${label} must be an ISO timestamp.`);
  }
  return timestamp;
}

export function compareIsoTimestamps(leftValue, rightValue) {
  const left = isoTimestamp(leftValue, "Left timestamp");
  const right = isoTimestamp(rightValue, "Right timestamp");
  const sortKey = (value) => {
    const match = UTC_RFC3339_PATTERN.exec(value);
    const fraction = (match?.[7] ?? "").padEnd(9, "0");
    return `${value.slice(0, 19)}.${fraction}`;
  };
  const leftKey = sortKey(left);
  const rightKey = sortKey(right);
  return leftKey < rightKey ? -1 : leftKey > rightKey ? 1 : 0;
}

export function timestampRange(createdValue, updatedValue, label = "Record") {
  const createdAt = isoTimestamp(createdValue, `${label} creation time`);
  const updatedAt = isoTimestamp(updatedValue, `${label} update time`);
  if (compareIsoTimestamps(updatedAt, createdAt) < 0) {
    throw new TypeError(`${label} update time must not precede its creation time.`);
  }
  return { createdAt, updatedAt };
}

export function defaultCreateId(prefix = "preset") {
  const cryptoRef = globalThis.crypto;
  if (cryptoRef && typeof cryptoRef.randomUUID === "function") {
    return `${prefix}_${cryptoRef.randomUUID()}`;
  }
  if (cryptoRef && typeof cryptoRef.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    cryptoRef.getRandomValues(bytes);
    return `${prefix}_${Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("")}`;
  }
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
}

export function defaultClock() {
  return new Date().toISOString();
}

export function contractFor(contracts, rawKey) {
  const key = collectionKey(rawKey);
  const contract = contracts instanceof Map ? contracts.get(key) : contracts?.[key];
  if (!isRecord(contract) || typeof contract.decodePayload !== "function") {
    throw new TypeError(`No strict preset contract is registered for ${key}.`);
  }
  if (contract.key !== undefined && contract.key !== key) {
    throw new TypeError(`Preset contract key mismatch for ${key}.`);
  }
  return contract;
}

export function decodePayload(contract, payload) {
  const decoded = contract.decodePayload(cloneValue(payload));
  if (!isRecord(decoded)) {
    throw new TypeError("Preset payload decoder must return an object.");
  }
  return cloneValue(decoded);
}

export function payloadsEqual(contract, left, right) {
  if (typeof contract.payloadsEqual === "function") {
    return contract.payloadsEqual(cloneValue(left), cloneValue(right)) === true;
  }
  return JSON.stringify(left) === JSON.stringify(right);
}

export function sourceRef(value) {
  if (!exactFields(value, ["id", "kind", "revisionId"])) {
    throw new TypeError("Preset source reference fields must match v1.");
  }
  const kind = value.kind;
  if (kind !== "preset" && kind !== "local" && kind !== "fixed" && kind !== "none") {
    throw new TypeError("Preset source reference kind is invalid.");
  }
  const id = kind === "none" ? "" : opaqueId(value.id, "Preset source ID");
  const revisionId = kind === "none"
    ? ""
    : opaqueId(value.revisionId, "Preset source revision ID");
  if ((kind === "none" && (value.id !== "" || value.revisionId !== ""))) {
    throw new TypeError("A detached preset source must have empty IDs.");
  }
  return { kind, id, revisionId };
}

export function detachedSource() {
  return { kind: "none", id: "", revisionId: "" };
}

export function fixedRows(contract) {
  const rawRows = typeof contract.fixedPresets === "function"
    ? contract.fixedPresets()
    : contract.fixedPresets;
  if (rawRows == null) {
    return [];
  }
  if (!Array.isArray(rawRows)) {
    throw new TypeError("Fixed preset resolver must return an array.");
  }
  const seen = new Set();
  return rawRows.map((raw, index) => {
    if (!isRecord(raw)) {
      throw new TypeError(`Fixed preset ${index + 1} must be an object.`);
    }
    const id = opaqueId(raw.id, "Fixed preset ID");
    if (seen.has(id)) {
      throw new TypeError(`Fixed preset ID ${id} is duplicated.`);
    }
    seen.add(id);
    return {
      kind: "fixed",
      id,
      name: boundedText(raw.name, "Fixed preset name", { max: 120 }),
      payload: decodePayload(contract, raw.payload),
      revisionId: raw.revisionId === undefined
        ? `fixed:${id}`
        : opaqueId(raw.revisionId, "Fixed preset revision ID"),
    };
  });
}

export function runtimeFixedPresetDescriptors(contract) {
  const configured = contract.runtimeFixedPresets ?? [];
  if (!Array.isArray(configured)) {
    throw new TypeError("Runtime fixed preset descriptors must be an array.");
  }
  const seen = new Set();
  return configured.map((descriptor, index) => {
    if (!exactFields(descriptor, RUNTIME_FIXED_PRESET_DESCRIPTOR_FIELDS)) {
      throw new TypeError(
        `Runtime fixed preset descriptor ${index + 1} must contain exactly id and name.`,
      );
    }
    const id = opaqueId(descriptor.id, "Runtime fixed preset ID");
    if (seen.has(id)) {
      throw new TypeError(`Runtime fixed preset ID ${id} is duplicated.`);
    }
    seen.add(id);
    return {
      id,
      name: boundedText(descriptor.name, "Runtime fixed preset name", { max: 120 }),
    };
  });
}

export function providerFailure(operation, error) {
  return {
    kind: "unavailable",
    operation,
    reason: typeof error?.code === "string" ? error.code : "indexeddb-failed",
  };
}
